import React, { useState, useEffect, useContext, useRef } from 'react';
import { models } from 'powerbi-client';
import * as api from "../../api/apiConnector.js";
import UserContext from '../../components/User_Context.js';
// import { getReportInGroup, getEmbedToken } from '../apiHelper/powerBiEmbedder.js';

/**
 * Componente riutilizzabile per l'embedding di report Power BI
 * @param {string} instance - Nome istanza (per caricare config lato server)
 * @param {string} reportKey - Chiave logica del report (oppure passare reportId)
 * @param {string} reportId - (opzionale) GUID del report se non si usa reportKey
 * @param {object} settings - Impostazioni opzionali per il report (default: filterPane e navPane abilitati)
 * @param {string} className - Classe CSS opzionale per il container
 * @param {object} style - Stile inline opzionale per il container
 */
const PowerBIReport = ({ 
    instance,
    reportKey,
    reportId, 
    settings = {
        filterPaneEnabled: true,
        navContentPaneEnabled: true
    },
    className = '',
    style = {}
}) => {
    const { currentUser } = useContext(UserContext);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [embedConfig, setEmbedConfig] = useState(null);
    const reportContainer = useRef(null);

    // Validazione props
    useEffect(() => {
        if (!instance || (!reportKey && !reportId)) {
            setError('instance e reportKey (o reportId) sono obbligatori');
            setIsLoading(false);
            return;
        }
    }, [instance, reportKey, reportId]);

    useEffect(() => {
        const getEmbedConfig = async () => {
            if (!instance || (!reportKey && !reportId)) return;
            
            setIsLoading(true);
            setError(null);
            
            try {
                const resp = await api.sendMessage("darm/power-bi/embed-config", {
                    user: currentUser(),
                    body: {
                        instance,
                        reportKey,
                        reportId
                    }
                });

                if (!resp || !resp.embedUrl || !resp.embedToken || !resp.reportId) {
                    throw new Error("Failed to get embed configuration.");
                }

                setEmbedConfig({
                    type: 'report',
                    tokenType: models.TokenType.Embed,
                    accessToken: resp.embedToken,
                    embedUrl: resp.embedUrl,
                    id: resp.reportId,
                    permissions: models.Permissions.All,
                    settings: settings
                });

            } catch (err) {
                console.error("Error in PowerBIReport getEmbedConfig:", err);
                setError('Failed to embed report: ' + (err.message || 'Unknown error'));
            } finally {
                setIsLoading(false);
            }
        };

        if (currentUser && currentUser()) {
            getEmbedConfig();
        } else {
            setError('User not authenticated.');
            setIsLoading(false);
        }
    }, [currentUser, instance, reportKey, reportId, settings]);

    useEffect(() => {
        if (embedConfig && reportContainer.current && window.powerbi) {
            window.powerbi.reset(reportContainer.current);
            const report = window.powerbi.embed(reportContainer.current, embedConfig);

            report.on('loaded', () => console.log('Report loaded'));
            report.on('rendered', () => {
                console.log('Report rendered');
                if (reportContainer.current) {
                    reportContainer.current.style.visibility = 'visible';
                }
            });
            report.on('error', (event) => {
                console.error("Error event from PowerBIEmbed:", event.detail);
                setError(event.detail?.message || 'An error occurred with the Power BI report.');
            });

            // Salva il riferimento al report per debug/accesso esterno
            window.report = report;

            return () => {
                if (window.powerbi && reportContainer.current) {
                    window.powerbi.reset(reportContainer.current);
                }
            };
        }
    }, [embedConfig]);

    if (error) {
        return <div style={{ color: 'red', padding: '1em' }}>Error: {error}</div>;
    }

    const defaultStyle = {
        height: '100%',
        width: '100%',
        visibility: isLoading ? 'hidden' : 'visible',
        flexGrow: 1,
        ...style
    };

    return (
        <div style={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }} className={className}>
            {isLoading && <div>Loading Power BI Report...</div>}
            <div
                ref={reportContainer}
                style={defaultStyle}
            />
        </div>
    );
};

export default PowerBIReport;